import { useQuery } from "@tanstack/react-query";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { ListChecks, Settings as SettingsIcon, ShieldCheck, Sparkles } from "lucide-react";
import { PlansPage } from "./pages/PlansPage";
import { PlanDetailPage } from "./pages/PlanDetailPage";
import { UpgradeReadinessPage } from "./pages/UpgradeReadinessPage";
import { LoginPage } from "./pages/LoginPage";
import { SettingsPage } from "./pages/SettingsPage";
import { LicenseBadge } from "./components/LicenseBadge";
import { TourLauncher, TourOverlay, tourForPath, useAutoStartTour } from "./components/tour";
import { platformApi, type ConfigResponse } from "./lib/api";

export function App() {
  return (
    <div className="grid min-h-screen grid-rows-[auto,1fr] bg-curate-fog">
      <Header />
      <main className="min-h-0">
        <Routes>
          <Route path="/" element={<Navigate to="/plans" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/plans/:planId" element={<PlanDetailPage />} />
          <Route path="/upgrade-readiness" element={<UpgradeReadinessPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>
      <RouteTourBinder />
      <TourOverlay />
    </div>
  );
}

function RouteTourBinder() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname);
  if (!tour) return null;
  return <BoundTour key={tour.id} />;
}

function BoundTour() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname)!;
  useAutoStartTour(tour);
  return null;
}

function Header() {
  const { data: config } = useQuery({
    queryKey: ["curate-config"],
    queryFn: async () => (await platformApi.get<ConfigResponse>("/config")).data,
    staleTime: 60_000,
    retry: false,
  });
  return (
    <header className="sticky top-0 z-20 border-b border-curate-slate/25 bg-white/85 backdrop-blur">
      <div className="flex w-full items-center gap-6 px-6 py-2.5">
        <a href="/" className="flex items-center gap-2.5 font-semibold text-curate-ink">
          <Logo />
          <div className="leading-tight">
            <div className="font-display text-[15px]">DatumCraft Curate</div>
            <div className="text-2xs font-normal text-curate-slate">
              BI cleanup, recommended and reviewed
            </div>
          </div>
        </a>
        <nav aria-label="primary" data-tour-id="nav" className="flex gap-1 text-sm">
          <NavItem to="/plans" icon={<ListChecks size={14} />}>Plans</NavItem>
          <NavItem to="/upgrade-readiness" icon={<ShieldCheck size={14} />}>
            Upgrade
          </NavItem>
          <NavItem to="/settings" icon={<SettingsIcon size={14} />}>Settings</NavItem>
        </nav>
        <div className="ml-auto flex items-center gap-2 text-2xs text-curate-slate">
          {config?.llmProvider && config.llmProvider !== "fallback" && (
            <span
              title={`LLM provider — ${config.llmProvider}`}
              className="inline-flex items-center gap-1 rounded-full bg-curate-primary/10 px-2 py-0.5 font-medium text-curate-primary"
            >
              <Sparkles size={11} /> {config.llmProvider}
            </span>
          )}
          <LicenseBadge />
          <TourLauncher />
        </div>
      </div>
    </header>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition-colors ${
          isActive
            ? "bg-curate-primary text-white shadow-sm"
            : "text-curate-ink hover:bg-curate-mist"
        }`
      }
    >
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </NavLink>
  );
}

// Product glyph — cluster of duplicates with one extracted in ember
// (the candidate to retire). Mirrors `GlyphCurate` from the website's
// brand system at `website/app/brand/products/glyphs.tsx`. Outline uses
// currentColor so it follows the parent text-* class; accent #E89B3A.
function Logo() {
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-curate-ink text-white shadow-sm"
      aria-hidden
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="8" cy="10" r="1.7" stroke="currentColor" strokeWidth={1.6} />
        <circle cx="11" cy="7.5" r="1.7" stroke="currentColor" strokeWidth={1.6} />
        <circle cx="11" cy="12.5" r="1.7" stroke="currentColor" strokeWidth={1.6} />
        <path
          d="M14 11 q3 1 4 5"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeDasharray="1.4 1.6"
          opacity="0.55"
        />
        <circle cx="18" cy="17" r="1.9" fill="#E89B3A" />
      </svg>
    </span>
  );
}
